import * as React from 'react';
import { Dispatch } from 'redux';
import { Translate } from 'react-redux-i18n';

import { StoreState } from '../../reducers';
import { DriveItemInfoMap } from '../../containers/DriveItemList/reducer';
import { ListDisplayType, DRIVE_ITEM_LIST_PAGE_SIZE } from '../../containers/DriveItemList/constants';
import { fetchDriveItem, toggleEditMode, getCustomTemplatesIds } from '../../containers/DriveItemList/actions';
import { Api } from '../../typings/api';
import Utils from '../../utils';
import DriveItem from '../../containers/DriveItem';
import {
	StyledDriveItemList,
	StyledInfiniteScroll,
	DriveItemListContainer,
	LoadingStatus,
	TitleWrapper,
	ButtonsWrapper,
	ButtonEdit,
	Loader,
} from './styles';
import Masonry from '../CustomMasonry';
import { AbstractDriveItemListPageParams } from '../../containers/DriveItemListPage/abstract';
import Title from '../../containers/Title';
import DesktopDragPreview from './DesktopDragPreview';
import TouchDragPreview from './TouchDragPreview';
import DragScroller, { DragScrollerPosition } from './DragScroller';
import FilesToast from '../../containers/ImportDesktopFile/Toast';
import FilesDndUploader from '../../containers/ImportDesktopFile/FilesDndUploader';
import UserAgentUtils from '../../utils/userAgent';
import CoverFlow from '../CoverFlow';
import DisplayTypeDropdown from './DisplayTypeDropdown';
import TagTrail from './TagTrail';
import { DriveItemListOrigin } from '../../containers/DriveItemListPage/constants';
import ApiUtils from '../../utils/api';
import { ButtonPDF } from './ButtonPDF/styles';

interface DriveItemListProps {
	dispatch: Dispatch<StoreState>;
	hasBottomBar: boolean;
	params?: AbstractDriveItemListPageParams;
	displayType: ListDisplayType;
	storeDriveItems: DriveItemInfoMap;
	navigationItems: JSX.Element[];
	additionalItems?: JSX.Element[] | null;
	driveItemIdsToDisplay: number[];
	isFetching: boolean;
	folderId?: number;
	showUnreadBadge: boolean;
	title?: string;
	hasActions?: boolean;
	hasSelectionIcon?: boolean;
	onDriveItemClick?: (item: Api.DriveItem) => void;
	sharedDirId: number | undefined;
	sourcesDirId: number | undefined;
	searchConfig: Api.SearchConfig | undefined;
	shouldGetCustomTemplates: boolean;
	isEditMode: boolean;
	isEditButtonDisplayed: boolean;
	isPrivateDir: boolean;
	isLoading: boolean;
	isTheaterOpened: boolean;
	isFilesDndEnabled?: boolean;
	shouldDisplayFilesToast?: boolean;
	isDisplayTypeDropdownDisplayed?: boolean;
	shouldResetPage?: boolean;
	isMultipleSelection?: boolean;
	selectedItemsIds?: number[];
}

interface DriveItemListState {
	page: number;
}

class DriveItemList extends React.Component<DriveItemListProps, DriveItemListState> {

	private driveItemListElement: HTMLElement;

	constructor(props: DriveItemListProps) {
		super(props);
		this.hasMoreDriveItems = this.hasMoreDriveItems.bind(this);
		this.loadMoreDriveItems = this.loadMoreDriveItems.bind(this);
		this.handleCoverFlowFolderChange = this.handleCoverFlowFolderChange.bind(this);
		this.handleCoverFlowSlideChange = this.handleCoverFlowSlideChange.bind(this);
		this.handleDownloadPDF = this.handleDownloadPDF.bind(this);
		this.state = { page: 0 };
	}

	componentDidMount() {
		const { shouldGetCustomTemplates, dispatch } = this.props;

		if (shouldGetCustomTemplates) {
			dispatch(getCustomTemplatesIds());
		}
	}

	componentDidUpdate(prevProps: DriveItemListProps) {
		const {
			driveItemIdsToDisplay,
			folderId,
			displayType,
			storeDriveItems,
			params,
			shouldResetPage,
			isTheaterOpened,
			dispatch,
		} = this.props;

		const isTheaterToggled = prevProps.isTheaterOpened !== isTheaterOpened;
		const isUrlChanged = prevProps.params && params && (
			prevProps.params.origin !== params.origin ||
			prevProps.params.originParam !== params.originParam);
		const isDisplayTypeChanged = prevProps.displayType !== displayType;
		const isResetNeeded = !prevProps.shouldResetPage && shouldResetPage;

		if ((!isTheaterToggled && isUrlChanged) || isDisplayTypeChanged || isResetNeeded) {
			this.setState({ page: 0 });
			this.resetEditMode();

			if (this.driveItemListElement) {
				this.driveItemListElement.scrollTop = 0;
			}
		}

		const nbItemToDisplay = Math.min(this.state.page * DRIVE_ITEM_LIST_PAGE_SIZE, driveItemIdsToDisplay.length);

		driveItemIdsToDisplay
			.slice(0, nbItemToDisplay)
			.forEach(driveItemId => {
				if (!storeDriveItems[driveItemId]) {
					dispatch(fetchDriveItem(driveItemId, folderId));
				}
			});

		// display all drive items in Coverflow mode in mobile app (as all items are already fetched)
		const shouldDisplayAllDriveItems = UserAgentUtils.isMobileApp && displayType === ListDisplayType.Coverflow;

		if (shouldDisplayAllDriveItems && nbItemToDisplay > 0 && nbItemToDisplay !== driveItemIdsToDisplay.length) {
			const pageCount = Math.ceil(driveItemIdsToDisplay.length / DRIVE_ITEM_LIST_PAGE_SIZE);
			this.setState({ page: pageCount });
		}
	}

	componentWillUnmount() {
		this.resetEditMode();
	}

	resetEditMode() {
		const { isEditMode, dispatch } = this.props;
		if (isEditMode) {
			dispatch(toggleEditMode());
		}
	}

	render() {
		const {
			displayType,
			driveItemIdsToDisplay,
			isFetching,
			showUnreadBadge,
			navigationItems,
			additionalItems,
			hasBottomBar,
			title,
			hasActions,
			hasSelectionIcon,
			isMultipleSelection,
			selectedItemsIds,
			onDriveItemClick,
			sharedDirId,
			sourcesDirId,
			searchConfig,
			isEditMode,
			isEditButtonDisplayed,
			isDisplayTypeDropdownDisplayed = true,
			isPrivateDir,
			isLoading,
			isFilesDndEnabled,
			shouldDisplayFilesToast,
			folderId,
			params,
			dispatch,
			storeDriveItems,
		} = this.props;
		const { page } = this.state;
		const { driveItemsToDisplay } = this;
		const isMasonry = displayType === ListDisplayType.Masonry;
		const isCoverFlow = displayType === ListDisplayType.Coverflow;
		const isLoadingNewItems = (driveItemsToDisplay.length < page * DRIVE_ITEM_LIST_PAGE_SIZE)
			&& driveItemsToDisplay.length < driveItemIdsToDisplay.length;
		const hasMoreDriveItems = this.hasMoreDriveItems();
		const additionalItemsCount = additionalItems ? additionalItems.length : 0;
		const itemsCount = additionalItemsCount + navigationItems.length + driveItemsToDisplay.length;
		const systemFoldersIds = [sharedDirId, sourcesDirId];
		const draggableItemsCount = isPrivateDir
			? driveItemsToDisplay.length - systemFoldersIds.length
			: itemsCount;
		const driveItemsElement = driveItemsToDisplay.map((driveItem: Api.DriveItem, index: number) => (
			<DriveItem
				key={driveItem.id}
				driveItem={driveItem}
				index={isPrivateDir ? index - systemFoldersIds.length : index}
				showUnreadBadge={showUnreadBadge}
				displayType={displayType}
				hasActions={hasActions}
				hasSelectionIcon={hasSelectionIcon}
				isMultipleSelection={isMultipleSelection}
				isSelectedInMultipleSelection={selectedItemsIds && selectedItemsIds.includes(driveItem.id)}
				isDraggable={!systemFoldersIds.includes(driveItem.id)}
				itemsCount={itemsCount}
				draggableItemsCount={draggableItemsCount}
				onClick={onDriveItemClick
					? () => onDriveItemClick(driveItem)
					: undefined}
			/>
		));

		let listItemsElement = navigationItems.concat(driveItemsElement);

		if (additionalItems) {
			listItemsElement = listItemsElement.concat(additionalItems);
		}

		const isEmpty = listItemsElement.length + driveItemIdsToDisplay.length === 0;
		const isSearchPage = params && params.origin === DriveItemListOrigin.Search;

		const infiniteScrollParams = {
			loadMore: this.loadMoreDriveItems,
			hasMore: !isLoadingNewItems && hasMoreDriveItems,
			useWindow: false,
		};

		let coverflowKey: string | number | undefined = folderId;
		if (!folderId && params && params.origin) {
			coverflowKey = params.origin;
			if (params.origin === 'search' && params.originParam) {
				coverflowKey = encodeURIComponent(params.originParam);
			}
		}

		let DriveItemListContainerClass = 'DriveItemListContainer';
		if (displayType) {
			DriveItemListContainerClass += ` displayType${displayType}`;
		}

		const shouldRenderDownloadPDF = isSearchPage && searchConfig && searchConfig.hasDownloadPDF && params && params.originParam;
		const shouldRenderTagTrail = isSearchPage && searchConfig && searchConfig.hasTagTrail;
		const categorySeparatorNew = searchConfig && searchConfig.categorySeparator;

		return (
			<>
				{isEditMode && (
					<>
						<DesktopDragPreview displayType={displayType} />
						<TouchDragPreview />
						<DragScroller
							position={DragScrollerPosition.top}
							container={this.driveItemListElement}
						/>
						<DragScroller
							position={DragScrollerPosition.bottom}
							container={this.driveItemListElement}
						/>
					</>
				)}
				<DriveItemListContainer
					isEmpty={isEmpty}
					hasBottomBar={hasBottomBar}
					className={DriveItemListContainerClass}
				>
					<TitleWrapper className="TitleWrapper">
						{title &&
							<Title title={title} />
						}
						{!isEmpty &&
							<ButtonsWrapper className="DriveItemListButtonsWrapper">
								{isEditButtonDisplayed && !isMasonry && !isCoverFlow && !UserAgentUtils.isWindowsApp() &&
									<ButtonEdit
										className="ButtonEdit"
										type="button"
										isEditMode={isEditMode}
										onClick={() => dispatch(toggleEditMode())}
									>
										<Translate value={isEditMode ? 'messages.done' : 'messages.move'} />
									</ButtonEdit>
								}
								{shouldRenderDownloadPDF &&
									<ButtonPDF className="ButtonPDF" onClick={this.handleDownloadPDF}>
										{Utils.getSvgIcon('iconset_uprez_download_bold')}
										<span>Download PDF</span>
									</ButtonPDF>
								}
								{isDisplayTypeDropdownDisplayed &&
									<DisplayTypeDropdown
										folderId={folderId}
										displayType={displayType}
										dispatch={dispatch}
									/>
								}
							</ButtonsWrapper>
						}
					</TitleWrapper>
					{shouldRenderTagTrail &&
						<TagTrail categorySeparator={categorySeparatorNew} originParam={params && params.originParam} />
					}

					<StyledDriveItemList
						ref={element => { this.driveItemListElement = element; }}
						isEditMode={isEditMode}
						isCoverFlow={isCoverFlow}
						className="StyledDriveItemList"
					>
						{isEmpty && !isLoadingNewItems && !isFetching
							? <div />
							: isCoverFlow
								? (
									<CoverFlow
										// key is used to reinitialize slider when change a folder
										key={coverflowKey}
										folderId={folderId}
										idsToDisplay={driveItemIdsToDisplay.filter(id => (
											!!storeDriveItems[id]
										))}
										driveItems={storeDriveItems}
										navigationItemsLength={navigationItems.length}
										additionalItemsLength={additionalItems ? additionalItems.length : 0}
										onFolderChange={this.handleCoverFlowFolderChange}
										onSlideChange={this.handleCoverFlowSlideChange}
									>
										{listItemsElement}
									</CoverFlow>
								)
								: (
									<StyledInfiniteScroll {...infiniteScrollParams}>
										{isMasonry
											? <Masonry>{listItemsElement}</Masonry>
											: listItemsElement
										}
									</StyledInfiniteScroll>
								)
						}
						{isFetching && hasMoreDriveItems && !isCoverFlow &&
							<LoadingStatus>
								{Utils.getSvgIcon('iconset_loading')}
								<Translate value="messages.loading" />
							</LoadingStatus>
						}
						{isLoading &&
							<Loader>
								{Utils.getSvgIcon('iconset_loading')}
								<Translate value="messages.loading" />
							</Loader>
						}
					</StyledDriveItemList>
				</DriveItemListContainer>
				{!UserAgentUtils.isMobileApp && isFilesDndEnabled && <FilesDndUploader />}
				{shouldDisplayFilesToast && <FilesToast />}
			</>
		);
	}

	private hasMoreDriveItems(): boolean {
		const { driveItemIdsToDisplay } = this.props;
		const pageCount = Math.ceil(driveItemIdsToDisplay.length / DRIVE_ITEM_LIST_PAGE_SIZE);
		return this.state.page < pageCount;
	}

	private loadMoreDriveItems(): void {
		const page = this.state.page + 1;
		this.setState({ page });
	}

	private get driveItemsToDisplay(): Api.DriveItem[] {
		const { driveItemIdsToDisplay, storeDriveItems } = this.props;
		const nbItemToDisplay = Math.min(this.state.page * DRIVE_ITEM_LIST_PAGE_SIZE, driveItemIdsToDisplay.length);

		return driveItemIdsToDisplay
			.slice(0, nbItemToDisplay)
			.map(driveItemId => storeDriveItems[driveItemId])
			.filter(driveItem => !!driveItem);
	}

	private handleCoverFlowFolderChange(): void {
		this.setState({ page: 1 });
	}

	private handleCoverFlowSlideChange(newIndex: number): void {
		const { navigationItems, additionalItems } = this.props;
		const additionalItemsLength = additionalItems ? additionalItems.length : 0;
		const displayedItemsLength = navigationItems.length + additionalItemsLength + this.driveItemsToDisplay.length;

		if (this.hasMoreDriveItems() && newIndex === displayedItemsLength - 1) {
			this.loadMoreDriveItems();
		}
	}

	private handleDownloadPDF(): void {
		const { params } = this.props;
		const originParam = params && params.originParam;
		if (originParam) {
			let url = ApiUtils.getDownloadPDF(originParam);
			if (ApiUtils.token) {
				url += `&token=${ApiUtils.token}`;
			}
			window.open(url);
		}
	}

}

export default DriveItemList;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/DriveItemList/index.tsx